import {
  ValuationHubCoreModelRealEstateEnumsObjectType,
  ValuationHubCoreModelRealEstateFinancialInformation,
  ValuationHubCoreModelRealEstatePropertyInformation,
  ValuationHubCoreModelRealEstateRentalInformation,
  ValuationHubCoreModelRealEstateValuationHubRealEstateWithoutAddress,
} from "@/api/generatedApi";
import {
  BuildingStandardDeterminationMethodEnum,
  EnergyAndPhotovoltaic,
  Modernizations,
  ProductTypeEnum,
  PropertyInformationWithoutImages,
} from "@/types/refineRealEstate";

import {
  buildingDataCompletionStatus,
  energyAndPhotovoltaicCompletionStatus,
  modernizationCompletionStatus,
  plotDataCompletionStatus,
} from "./realEstateDataValidationAndCompletenessHelpers";

type PropertyInformationWithCommercialData = ValuationHubCoreModelRealEstatePropertyInformation & {
  commercial_area?: number | null;
  commercial_units?: number | null;
};

/**
 * Maps application state (PropertyInformationWithoutImages) to API request body format
 */
export const mapStateToApiRequestBody = (
  propertyInformationState: PropertyInformationWithoutImages,
): ValuationHubCoreModelRealEstateValuationHubRealEstateWithoutAddress => {
  return {
    property_information: {
      object_type: propertyInformationState.generalInfo.objectType as ValuationHubCoreModelRealEstateEnumsObjectType,
      building_type: propertyInformationState.generalInfo.buildingType,
      house_or_apartment_type: propertyInformationState.buildingData.houseOrApartmentType,
      ownership: propertyInformationState.generalInfo.ownership,
      construction_year: propertyInformationState.buildingData.constructionYear,
      living_area: propertyInformationState.buildingData.livingArea,
      commercial_area: propertyInformationState.buildingData.commercialArea,
      commercial_units: propertyInformationState.buildingData.commercialUnits,
      plot_area: propertyInformationState.plotData.plotArea,
      number_of_floors: propertyInformationState.buildingData.numberOfFloors,
      floor_number: propertyInformationState.buildingData.floorNumber,
      rooms: propertyInformationState.buildingData.rooms,
      basement: propertyInformationState.buildingData.basement,
      basement_conversion: propertyInformationState.buildingData.basementConversion,
      construction: propertyInformationState.buildingData.construction,
      garages: propertyInformationState.buildingData.garages,
      outdoor_parking_spaces: propertyInformationState.buildingData.outdoorParkingSpaces,
      elevator: propertyInformationState.buildingData.elevator,
      number_of_units: propertyInformationState.buildingData.unitsNumber,
      roof: propertyInformationState.buildingData.roof,
      outdoor_facilities: propertyInformationState.buildingData.outdoorFacilities,
      garden: propertyInformationState.buildingData.garden,
      equipment: {
        method: propertyInformationState.buildingStandard.method,
        value:
          propertyInformationState.buildingStandard.method === BuildingStandardDeterminationMethodEnum.simple
            ? propertyInformationState.buildingStandard.value
            : undefined,
        matrix: {
          insulated_exterior_walls: propertyInformationState.buildingStandard.matrix?.insulatedExteriorWalls,
          bathroom: propertyInformationState.buildingStandard.matrix?.bathroom,
          roof_covering: propertyInformationState.buildingStandard.matrix?.roofCovering,
          windows: propertyInformationState.buildingStandard.matrix?.windows,
          guest_toilet: propertyInformationState.buildingStandard.matrix?.guestToilet,
          heating: propertyInformationState.buildingStandard.matrix?.heating,
          heating_system: propertyInformationState.buildingStandard.matrix?.heatingSystem,
          surface_mounted_installation: propertyInformationState.buildingStandard.matrix?.surfaceMountedInstallation,
          store_room: propertyInformationState.buildingStandard.matrix?.storeRoom,
          floor_covering: propertyInformationState.buildingStandard.matrix?.floorCovering,
          residential_area: propertyInformationState.buildingStandard.matrix?.residentialArea,
          sauna: propertyInformationState.buildingStandard.matrix?.sauna,
          fireplace: propertyInformationState.buildingStandard.matrix?.fireplace,
        },
      },
      modernization: {
        method: propertyInformationState.modernization.method,
        class: propertyInformationState.modernization.class,
        cost: propertyInformationState.modernization.cost,
        cost_description: propertyInformationState.modernization.costDescription,
        refurbishment_year: propertyInformationState.modernization.refurbishmentYear,
        matrix: {
          interior_design: propertyInformationState.modernization.matrix?.interiorDesign,
          sanitary_area: propertyInformationState.modernization.matrix?.sanitaryArea,
          heating_system: propertyInformationState.modernization.matrix?.heatingSystem,
          pipes: propertyInformationState.modernization.matrix?.pipes,
          windows: propertyInformationState.modernization.matrix?.windows,
          thermal_insulation: propertyInformationState.modernization.matrix?.thermalInsulation,
          roof_covering_and_insulation: propertyInformationState.modernization.matrix?.roofCoveringAndInsulation,
          room_layout: propertyInformationState.modernization.matrix?.roomLayout,
        },
      },
      energy_efficiency_house_standard: propertyInformationState.energyAndPhotovoltaic.energyEfficiencyHouseStandard,
      energy_certificate: propertyInformationState.energyAndPhotovoltaic.energyCertificate
        ? {
            energy_class: propertyInformationState.energyAndPhotovoltaic.energyCertificate || undefined,
            issue_date: propertyInformationState.energyAndPhotovoltaic.issueDate
              ? propertyInformationState.energyAndPhotovoltaic.issueDate.split("T")[0]
              : undefined,
            certificate_type: propertyInformationState.energyAndPhotovoltaic.certificateType || undefined,
            final_energy_demand: propertyInformationState.energyAndPhotovoltaic.finalEnergy || undefined,
            primary_energy_demand: propertyInformationState.energyAndPhotovoltaic.primaryEnergy || undefined,
            co2_emission: propertyInformationState.energyAndPhotovoltaic.CO2Emissions ?? undefined,
          }
        : undefined,
      photovoltaic:
        propertyInformationState.energyAndPhotovoltaic.photovoltaicAvailable === true
          ? {
              construction_year: propertyInformationState.energyAndPhotovoltaic.constructionYear,
              annual_power_yield: propertyInformationState.energyAndPhotovoltaic.annualPowerYield,
            }
          : undefined,
    } as PropertyInformationWithCommercialData,
    financial_information: {
      price: propertyInformationState.financialAndRentalInfo.price,
      rent: propertyInformationState.financialAndRentalInfo.rent,
    },
    rental_information: {
      current_usage: propertyInformationState.generalInfo.actualUse,
      suitable_for_own_use: propertyInformationState.generalInfo.selfUseSuitability,
    },
  };
};

export const mapApiResponseToPropertyInfo = (
  property_information: ValuationHubCoreModelRealEstatePropertyInformation,
  rental_information: ValuationHubCoreModelRealEstateRentalInformation | null | undefined,
  financial_information: ValuationHubCoreModelRealEstateFinancialInformation | null | undefined,
): PropertyInformationWithoutImages => {
  const propertyInformationWithCommercialArea = property_information as PropertyInformationWithCommercialData;
  const buildingData = {
    constructionYear: property_information.construction_year ?? undefined,
    construction: property_information.construction ?? undefined,
    livingArea: property_information.living_area ?? undefined,
    commercialArea: propertyInformationWithCommercialArea.commercial_area ?? undefined,
    commercialUnits: propertyInformationWithCommercialArea.commercial_units ?? undefined,
    rooms: property_information.rooms ?? undefined,
    floorNumber: property_information.floor_number ?? undefined,
    houseOrApartmentType: property_information.house_or_apartment_type ?? undefined,
    numberOfFloors: property_information.number_of_floors ?? undefined,
    unitsNumber: property_information.number_of_units ?? undefined,
    roof: property_information.roof ?? undefined,
    basement: property_information.basement ?? undefined,
    basementConversion: property_information.basement_conversion ?? undefined,
    outdoorFacilities: property_information.outdoor_facilities ?? undefined,
    garden: property_information.garden ?? undefined,
    elevator: property_information.elevator ?? undefined,
    outdoorParkingSpaces: property_information.outdoor_parking_spaces ?? undefined,
    garages: property_information.garages ?? undefined,
  };

  return {
    generalInfo: {
      productType: ProductTypeEnum.marketValue,
      objectType: property_information.object_type,
      buildingType: property_information.building_type ?? undefined,
      ownership: property_information.ownership ?? undefined,
      actualUse: rental_information?.current_usage ?? undefined,
      selfUseSuitability: rental_information?.suitable_for_own_use ?? undefined,
      completionStatus: "completedFull",
    },
    plotData: {
      plotArea: property_information.plot_area ?? undefined,
      completionStatus: plotDataCompletionStatus(property_information.plot_area),
    },
    buildingData: {
      ...buildingData,
      completionStatus: buildingDataCompletionStatus(
        buildingData,
        property_information.object_type,
        property_information.building_type,
      ),
    },
    energyAndPhotovoltaic: ((): EnergyAndPhotovoltaic => {
      const photovoltaicAvailable =
        property_information.photovoltaic?.construction_year != null &&
        property_information.photovoltaic?.annual_power_yield != null;

      const energyAndPhotovoltaic = {
        energyCertificate: property_information.energy_certificate?.energy_class ?? undefined,
        energyEfficiencyHouseStandard: property_information.energy_efficiency_house_standard ?? undefined,
        issueDate: property_information.energy_certificate?.issue_date ?? undefined,
        certificateType: property_information.energy_certificate?.certificate_type ?? undefined,
        finalEnergy: property_information.energy_certificate?.final_energy_demand ?? undefined,
        primaryEnergy: property_information.energy_certificate?.primary_energy_demand ?? undefined,
        CO2Emissions: property_information.energy_certificate?.co2_emission ?? undefined,
        photovoltaicAvailable,
        constructionYear: photovoltaicAvailable
          ? property_information.photovoltaic?.construction_year ?? undefined
          : undefined,
        annualPowerYield: photovoltaicAvailable
          ? property_information.photovoltaic?.annual_power_yield ?? undefined
          : undefined,
      };
      return {
        ...energyAndPhotovoltaic,
        completionStatus: energyAndPhotovoltaicCompletionStatus(
          energyAndPhotovoltaic,
          property_information.construction_year,
        ),
      };
    })(),
    buildingStandard: {
      method: property_information.equipment?.method ?? undefined,
      matrix: {
        insulatedExteriorWalls: property_information.equipment?.matrix?.insulated_exterior_walls,
        storeRoom: property_information.equipment?.matrix?.store_room,
        bathroom: property_information.equipment?.matrix?.bathroom,
        sauna: property_information.equipment?.matrix?.sauna,
        roofCovering: property_information.equipment?.matrix?.roof_covering,
        windows: property_information.equipment?.matrix?.windows,
        floorCovering: property_information.equipment?.matrix?.floor_covering,
        guestToilet: property_information.equipment?.matrix?.guest_toilet,
        heating: property_information.equipment?.matrix?.heating,
        heatingSystem: property_information.equipment?.matrix?.heating_system,
        surfaceMountedInstallation: property_information.equipment?.matrix?.surface_mounted_installation,
        fireplace: property_information.equipment?.matrix?.fireplace,
        residentialArea: property_information.equipment?.matrix?.residential_area,
      },
      value: property_information.equipment?.value,
      completionStatus: "completedFull",
    },
    modernization: ((): Modernizations => {
      const modernization = {
        method: property_information.modernization?.method ?? undefined,
        refurbishmentYear: property_information.modernization?.refurbishment_year ?? undefined,
        matrix: {
          interiorDesign: property_information.modernization?.matrix?.interior_design,
          sanitaryArea: property_information.modernization?.matrix?.sanitary_area,
          heatingSystem: property_information.modernization?.matrix?.heating_system,
          pipes: property_information.modernization?.matrix?.pipes,
          windows: property_information.modernization?.matrix?.windows,
          thermalInsulation: property_information.modernization?.matrix?.thermal_insulation,
          roofCoveringAndInsulation: property_information.modernization?.matrix?.roof_covering_and_insulation,
          roomLayout: property_information.modernization?.matrix?.room_layout,
        },
        class: property_information.modernization?.class ?? undefined,
        cost: property_information.modernization?.cost ?? undefined,
        costDescription: property_information.modernization?.cost_description ?? undefined,
      };
      return {
        ...modernization,
        completionStatus: modernizationCompletionStatus(modernization, property_information.construction_year),
      };
    })(),
    financialAndRentalInfo: {
      price: {
        value: financial_information?.price?.value ?? undefined,
      },
      completionStatus: "completedFull",
    },
  };
};
